import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  getInquiryDraftAdmin,
  deleteInquiryDraftAdmin,
} from "@/lib/inquiry-draft.functions";
import { toast } from "sonner";
import {
  Loader2,
  Trash2,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Users as UsersIcon,
} from "lucide-react";
import { PageHeader } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/drafts/$id")({
  component: DraftDetailPage,
});

type Draft = {
  id: string;
  session_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  event_date: string | null;
  location: string | null;
  guests: number | null;
  message: string | null;
  products: { slug: string; name: string; image: string; quantity?: number }[];
  created_at: string;
  updated_at: string;
};

function DraftDetailPage() {
  const { id } = Route.useParams();
  const get = useServerFn(getInquiryDraftAdmin);
  const del = useServerFn(deleteInquiryDraftAdmin);
  const navigate = useNavigate();
  const [i, setI] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get({ data: { id } })
      .then((d) => setI(d as unknown as Draft | null))
      .catch((e: any) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const remove = async () => {
    if (!i) return;
    if (!confirm("Ta bort utkast?")) return;
    try {
      await del({ data: { id: i.id } });
      toast.success("Borttaget");
      navigate({ to: "/admin/drafts" });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!i) return <p className="text-sm text-muted-foreground">Hittades inte.</p>;

  return (
    <>
      <PageHeader title={i.name || "Utkast utan namn"} backTo="/admin/drafts">
        {i.email && (
          <a
            href={`mailto:${i.email}?subject=Din påbörjade förfrågan`}
            className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-bold"
          >
            <Mail className="h-4 w-4" /> Kontakta
          </a>
        )}
        <button
          onClick={remove}
          className="inline-flex items-center gap-2 rounded-full bg-destructive text-destructive-foreground px-4 py-2 text-sm font-bold"
        >
          <Trash2 className="h-4 w-4" /> Ta bort
        </button>
      </PageHeader>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            Påbörjad — ej inskickad
          </span>
          <span className="text-xs text-muted-foreground">
            Påbörjad {new Date(i.created_at).toLocaleString("sv-SE")} · senast ändrad {new Date(i.updated_at).toLocaleString("sv-SE")}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {i.email && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <a href={`mailto:${i.email}`} className="hover:underline">
                {i.email}
              </a>
            </div>
          )}
          {i.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <a href={`tel:${i.phone}`} className="hover:underline">
                {i.phone}
              </a>
            </div>
          )}
          {i.event_date && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {new Date(i.event_date).toLocaleDateString("sv-SE")}
            </div>
          )}
          {i.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              {i.location}
            </div>
          )}
          {i.guests != null && (
            <div className="flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-muted-foreground" />
              {i.guests} gäster
            </div>
          )}
        </div>

        {i.message && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
              Meddelande
            </div>
            <p className="text-sm whitespace-pre-wrap rounded-lg bg-secondary/40 p-3">
              {i.message}
            </p>
          </div>
        )}

        {i.products.length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
              Produkter ({i.products.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {i.products.map((p, k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs"
                >
                  {p.name}
                  {p.quantity ? ` × ${p.quantity}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
